import {
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  InputAdornment
} from '@mui/material'

import { Table } from 'react-bootstrap'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import DropdownActionButtonMui from '../../components/action_button/DropdownActionButtonMui'
import DetailsSummarySection from '../../components/details/summary/DetailsSummarySection'
import EvidenceLightbox from '../../components/EvidenceLightbox'
import GuidanceNote from '../../components/GuidanceNote'
import PaymentMethodsSummary from '../../components/PaymentMethodsSummary'
import SectionCollapse from '../../components/SectionCollapse'
import SettlementDetailContextHeader from '../../components/SettlementDetailContextHeader'
import TaskConfirmDialog from '../../components/dialogs/TaskConfirmDialog'
import FormDialog from '../../components/dialogs/FormDialog'
import SingleDateDepositPicker from './components/tabs/SingleDateDepositPicker'
import SummaryLine from './components/tabs/SummaryLine'
import {
  SETTLEMENT_DETAILS_CASH_COUNT_CLOSE_LABEL,
  SETTLEMENT_DETAILS_CASH_COUNT_DENOMINATION_HEADER,
  SETTLEMENT_DETAILS_CASH_COUNT_OPEN_LABEL,
  SETTLEMENT_DETAILS_CASH_COUNT_QUANTITY_HEADER,
  SETTLEMENT_DETAILS_CASH_COUNT_SECTION_TITLE,
  SETTLEMENT_DETAILS_CASH_COUNT_TABLE_ARIA_LABEL,
  SETTLEMENT_DETAILS_CASH_COUNT_TOTAL_HEADER,
  SETTLEMENT_DETAILS_CASH_COUNT_TOTAL_LABEL,
  SETTLEMENT_DETAILS_CASH_DIFFERENCE_LABEL,
  SETTLEMENT_DETAILS_CASH_REPORTED_LABEL,
  SETTLEMENT_DETAILS_COLLECTED_CASH_LABEL,
  SETTLEMENT_DETAILS_COLLECTED_CHECKS_LABEL,
  SETTLEMENT_DETAILS_DELIVERY_CREDIT_SUBTITLE,
  SETTLEMENT_DETAILS_DELIVERY_CREDIT_TITLE,
  SETTLEMENT_DETAILS_DEPOSITS_DATE_PLACEHOLDER,
  SETTLEMENT_DETAILS_DEPOSITS_EMPTY_STATE,
  SETTLEMENT_DETAILS_DEPOSITS_HEADER_ACTION,
  SETTLEMENT_DETAILS_DEPOSITS_HEADER_AMOUNT,
  SETTLEMENT_DETAILS_DEPOSITS_HEADER_BANK,
  SETTLEMENT_DETAILS_DEPOSITS_HEADER_DATE,
  SETTLEMENT_DETAILS_DEPOSITS_HEADER_IMAGE,
  SETTLEMENT_DETAILS_DEPOSITS_HEADER_NUMBER,
  SETTLEMENT_DETAILS_DEPOSITS_HEADER_UID,
  SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_PLURAL,
  SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_PREFIX,
  SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_SINGULAR,
  SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_SUFFIX,
  SETTLEMENT_DETAILS_DEPOSITS_AMOUNT_INPUT_LABEL,
  SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE,
  SETTLEMENT_DETAILS_DEPOSITS_NUMBER_INPUT_LABEL,
  SETTLEMENT_DETAILS_DEPOSITS_REQUIRED_FIELD_MESSAGE,
  SETTLEMENT_DETAILS_DEPOSITS_TITLE,
  SETTLEMENT_DETAILS_DEPOSIT_UPDATE_ERROR,
  SETTLEMENT_DETAILS_DISCARD_CHANGES_CANCEL,
  SETTLEMENT_DETAILS_DISCARD_CHANGES_CONFIRM,
  SETTLEMENT_DETAILS_DISCARD_CHANGES_MESSAGE,
  SETTLEMENT_DETAILS_DISCARD_CHANGES_TITLE,
  SETTLEMENT_DETAILS_EDIT_ACTION_LABEL,
  SETTLEMENT_DETAILS_LIQUIDATION_CLOSE_LABEL,
  SETTLEMENT_DETAILS_LIQUIDATION_OPEN_LABEL,
  SETTLEMENT_DETAILS_LIQUIDATION_SECTION_TITLE,
  SETTLEMENT_DETAILS_NOTE_MESSAGE,
  SETTLEMENT_DETAILS_NOTE_TITLE,
  SETTLEMENT_DETAILS_PAYMENT_METHODS_CLOSE_LABEL,
  SETTLEMENT_DETAILS_PAYMENT_METHODS_OPEN_LABEL,
  SETTLEMENT_DETAILS_PAYMENT_METHODS_SECTION_TITLE,
  SETTLEMENT_DETAILS_RECEIPT_TYPE_DEPOSIT,
  SETTLEMENT_DETAILS_SUMMARY_CLOSE_LABEL,
  SETTLEMENT_DETAILS_SUMMARY_OPEN_LABEL,
  SETTLEMENT_DETAILS_SUMMARY_SECTION_TITLE,
  SETTLEMENT_DETAILS_TAB_TITLE,
  SETTLEMENT_DETAILS_TOTAL_COLLECTED_LABEL,
  SETTLEMENT_DETAILS_TOTAL_COLLECTED_SUBTITLE,
  SETTLEMENT_DETAILS_POLICE_PAYMENT_TITLE,
  SETTLEMENT_DETAILS_POLICE_PAYMENT_SUBTITLE,
  SETTLEMENT_DETAILS_TOTAL_DEPOSITED_LABEL,
  SETTLEMENT_DETAILS_TOTAL_TO_DEPOSIT_LABEL
} from '../../constants/settlementDetailsConstants'
import {
  UI_COLOR_ACCENT_ORANGE,
  UI_COLOR_ACCENT_ORANGE_ALT,
  UI_COLOR_ACCENT_ORANGE_ALT_HOVER,
  UI_COLOR_BORDER_CARD,
  UI_COLOR_BORDER_MEDIUM,
  UI_COLOR_DANGER,
  UI_COLOR_DANGER_ALT,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_PRIMARY_LINK,
  UI_COLOR_POLICE_PAYMENT_ACCENT,
  UI_COLOR_POLICE_PAYMENT_BG,
  UI_COLOR_POLICE_PAYMENT_BORDER,
  UI_COLOR_SUCCESS,
  UI_COLOR_SUCCESS_ALT,
  UI_COLOR_SURFACE_INFO,
  UI_COLOR_SURFACE_PRIMARY_SOFT,
  UI_COLOR_TEXT_HEADING,
  UI_COLOR_TEXT_ICON_MUTED,
  UI_COLOR_TEXT_INFO,
  UI_COLOR_TEXT_MUTED_LIGHT,
  UI_COLOR_TEXT_SUBTITLE,
  UI_COLOR_TEXT_TITLE_ALT,
  UI_COLOR_WARNING_SOFT,
  UI_COLOR_WARNING_TEXT,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import ImageUploading from 'react-images-uploading'
import GoogleIcon from '../../icon/GoogleIcon'
import { convertByteToMb, formatMoney } from '../../helpers/utils'
import {
  buildPaymentMethodCards,
  buildReceiptRows,
  getDeliveryCreditAmount,
  getPolicePaymentAmount,
  getTotalCollectedAmount,
  getTotalToDeposit,
  isPolicePaymentEnabled,
  resolveApprovalStatus
} from '../helpers/detailsHelpers'
import { APPROVAL_STATUS } from '../helpers/detailsConstants'
import {
  ACTION_COLUMN_WIDTH,
  DEPOSIT_BALANCED_COLOR,
  DEPOSIT_DIALOG_AMOUNT_PLACEHOLDER,
  DEPOSIT_DIALOG_BANK_PLACEHOLDER,
  DEPOSIT_DIALOG_ERROR_TEXT_SX,
  DEPOSIT_DIALOG_INPUT_HEIGHT,
  DEPOSIT_DIALOG_NUMBER_PLACEHOLDER,
  DEPOSIT_NUMBER_MAX_LENGTH,
  DELETE_DIALOG_ACTIONS_SX,
  DELETE_DIALOG_CANCEL_BUTTON_SX,
  DELETE_DIALOG_CONFIRM_BUTTON_SX,
  DELETE_DIALOG_CONTENT_SX,
  DELETE_DIALOG_MESSAGE_SX,
  DELETE_DIALOG_PAPER_SX,
  DELETE_DIALOG_TITLE_SX,
  DISCARD_DIALOG_CONFIRM_BUTTON_SX,
  EDITABLE_INPUT_HEIGHT,
  LIQUIDATION_DETAILS_GRID_COLUMNS
} from '../helpers/settlementDetailsTabConstants'
import {
  areReceiptRowsEqual,
  buildLiquidationDetailsFields,
  createReceiptDraft,
  formatDepositAmountForDisplay,
  formatDepositDate,
  getBankOptionLabel,
  getBalanceStatusText,
  getCashCountModel,
  getDepositAmountExceededMessage,
  getDepositUiChangeKey,
  getDepositUiChanges,
  getDepositUiChangesForDisplay,
  isDepositDraftFieldInvalid,
  normalizeDepositAmountInput,
  normalizeDepositNumber,
  parseDepositAmount,
  serializeDepositsForApproval,
  validateDepositDraft
} from '../helpers/settlementDetailsTabHelpers'
import React from 'react'

const DEPOSIT_UNBALANCED_COLOR = UI_COLOR_DANGER
const CASH_DIFF_GREEN = UI_COLOR_SUCCESS_ALT
const CASH_DIFF_RED = UI_COLOR_DANGER_ALT

function MetricCard({ icon, title, subtitle, amount, accent = UI_COLOR_TEXT_HEADING }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `2px solid ${UI_COLOR_BORDER_MEDIUM}`,
        borderRadius: 2,
        px: 2,
        py: 1.5,
        minHeight: 86
      }}
    >
      <Box display="flex" alignItems="center" gap={1.25}>
        <GoogleIcon name={icon} size={22} color={UI_COLOR_PRIMARY_ALT} />
        <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>
          {title}
        </Typography>
      </Box>
      <Typography sx={{ color: UI_COLOR_TEXT_SUBTITLE, fontSize: 11, mt: 1 }}>
        {subtitle}
      </Typography>
      <Typography sx={{ color: accent, fontSize: 22, fontWeight: 900, mt: 0.5 }}>
        {formatMoney(amount)}
      </Typography>
    </Paper>
  )
}

function PolicePaymentCard({ amount }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${UI_COLOR_POLICE_PAYMENT_BORDER}`,
        borderRadius: 2,
        backgroundColor: UI_COLOR_POLICE_PAYMENT_BG,
        px: 2,
        py: 1.25,
        mt: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <Box
        display="grid"
        gridTemplateColumns="22px auto"
        columnGap={1.25}
        rowGap={0.25}
        alignItems="center"
      >
        <GoogleIcon name="local_police" size={22} color={UI_COLOR_POLICE_PAYMENT_ACCENT} />
        <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>
          {SETTLEMENT_DETAILS_POLICE_PAYMENT_TITLE}
        </Typography>
        <Typography
          sx={{
            color: UI_COLOR_TEXT_SUBTITLE,
            fontSize: 11,
            gridColumn: 1,
            gridRow: 2,
            whiteSpace: 'nowrap'
          }}
        >
          {SETTLEMENT_DETAILS_POLICE_PAYMENT_SUBTITLE}
        </Typography>
      </Box>
      <Typography
        sx={{ color: UI_COLOR_POLICE_PAYMENT_ACCENT, fontSize: 22, fontWeight: 900, flexShrink: 0 }}
      >
        {formatMoney(amount)}
      </Typography>
    </Paper>
  )
}

function CashCountSummary({ settlement }) {
  const cashCount = getCashCountModel(settlement)

  if (!cashCount) return null

  const differenceColor =
    cashCount.closureTotal > cashCount.reportedCash
      ? CASH_DIFF_GREEN
      : cashCount.reportedCash > cashCount.closureTotal
        ? CASH_DIFF_RED
        : UI_COLOR_TEXT_HEADING

  return (
    <SectionCollapse
      title={SETTLEMENT_DETAILS_CASH_COUNT_SECTION_TITLE}
      type="title"
      defaultOpen={false}
      ariaLabelOpen={SETTLEMENT_DETAILS_CASH_COUNT_OPEN_LABEL}
      ariaLabelClose={SETTLEMENT_DETAILS_CASH_COUNT_CLOSE_LABEL}
      icon={<GoogleIcon name="calculate" size={20} color={UI_COLOR_PRIMARY_ALT} />}
    >
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: `1px solid ${UI_COLOR_BORDER_CARD}`,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 1.5
        }}
      >
        <MuiTable size="small" aria-label={SETTLEMENT_DETAILS_CASH_COUNT_TABLE_ARIA_LABEL}>
          <TableHead className="table-header-gray-mui">
            <TableRow>
              <TableCell>{SETTLEMENT_DETAILS_CASH_COUNT_DENOMINATION_HEADER}</TableCell>
              <TableCell align="center">{SETTLEMENT_DETAILS_CASH_COUNT_QUANTITY_HEADER}</TableCell>
              <TableCell align="right">{SETTLEMENT_DETAILS_CASH_COUNT_TOTAL_HEADER}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cashCount.rows.map(({ denomination, quantity, total }) => (
              <TableRow key={denomination}>
                <CashCountValueCell>{formatMoney(denomination)}</CashCountValueCell>
                <CashCountValueCell align="center">{quantity}</CashCountValueCell>
                <CashCountValueCell align="right">{formatMoney(total)}</CashCountValueCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 1.5
        }}
      >
        <CashTotalCard
          label={SETTLEMENT_DETAILS_CASH_REPORTED_LABEL}
          amount={cashCount.reportedCash}
        />
        <CashTotalCard
          label={SETTLEMENT_DETAILS_CASH_COUNT_TOTAL_LABEL}
          amount={cashCount.closureTotal}
        />
        <CashTotalCard
          label={SETTLEMENT_DETAILS_CASH_DIFFERENCE_LABEL}
          amount={cashCount.cashDifference}
          amountColor={differenceColor}
        />
      </Box>
    </SectionCollapse>
  )
}

function CashCountValueCell({ children, align = 'left' }) {
  return (
    <TableCell
      align={align}
      sx={{
        color: UI_COLOR_TEXT_HEADING,
        fontSize: 14,
        fontWeight: 700,
        py: 1.25
      }}
    >
      {children}
    </TableCell>
  )
}

function CashTotalCard({ label, amount, amountColor = UI_COLOR_TEXT_HEADING }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `2px solid ${UI_COLOR_BORDER_MEDIUM}`,
        borderRadius: 2,
        px: 2,
        py: 1.5,
        minHeight: 82
      }}
    >
      <Typography
        sx={{
          color: UI_COLOR_TEXT_TITLE_ALT,
          fontSize: 12,
          fontWeight: 800,
          textTransform: 'uppercase'
        }}
      >
        {label}:
      </Typography>
      <Typography sx={{ color: amountColor, fontSize: 20, fontWeight: 900, mt: 0.75 }}>
        {formatMoney(amount || 0)}
      </Typography>
    </Paper>
  )
}

export default function SettlementDetailsTab({
  settlement,
  actions = null,
  bankOptions = [],
  discardChangesToken = 0,
  onDirtyChange = () => {},
  onDepositsChange = () => {},
  onCreateDeposit = null,
  onUpdateDeposit = null,
  onDeleteDeposit = null,
  onNotify = () => {},
  depositsSaving = false,
  canWrite = false
}) {
  const [lightbox, setLightbox] = React.useState({ open: false, images: [], index: 0 })
  const [receiptRows, setReceiptRows] = React.useState([])
  const [editingRowId, setEditingRowId] = React.useState(null)
  const [draftRow, setDraftRow] = React.useState(null)
  const [pendingNavigationHref, setPendingNavigationHref] = React.useState(null)
  const [showDiscardDialog, setShowDiscardDialog] = React.useState(false)
  const [isSavingDeposits, setIsSavingDeposits] = React.useState(false)
  const [draftErrors, setDraftErrors] = React.useState({})
  const [depositDialog, setDepositDialog] = React.useState(null)
  const lastDepositDialogModeRef = React.useRef('create')
  const [receiptImage, setReceiptImage] = React.useState('')
  const [receiptImages, setReceiptImages] = React.useState([])
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false)

  const [editedDepositChanges, setEditedDepositChanges] = React.useState({})

  if (depositDialog) lastDepositDialogModeRef.current = depositDialog

  const renderedDepositDialog = depositDialog || lastDepositDialogModeRef.current
  const initialReceiptRows = React.useMemo(
    () =>
      buildReceiptRows(settlement).filter(
        (row) => row.type === SETTLEMENT_DETAILS_RECEIPT_TYPE_DEPOSIT
      ),
    [settlement]
  )

  const methods = buildPaymentMethodCards(settlement)
  const totalCollected = getTotalCollectedAmount(settlement)
  const deliveryCreditAmount = getDeliveryCreditAmount(settlement)
  const policeEnabled = isPolicePaymentEnabled(settlement)
  const policePaymentAmount = getPolicePaymentAmount(settlement)
  const liquidationDetailsFields = buildLiquidationDetailsFields(settlement)
  const totalToDeposit = getTotalToDeposit(settlement)
  const totalDeposited = receiptRows.reduce((sum, row) => sum + Number(row.amount || 0), 0)
  const balanced = Math.round(totalToDeposit * 100) === Math.round(totalDeposited * 100)
  const approvalStatus = resolveApprovalStatus(settlement)
  const isApproved = [
    APPROVAL_STATUS.SENT,
    APPROVAL_STATUS.RECEIVED,
    APPROVAL_STATUS.INSERTED_ERP
  ].includes(approvalStatus)
  const BalanceStatusIcon = balanced ? CheckCircleOutlineIcon : HighlightOffIcon
  const balanceStatusColor = balanced ? DEPOSIT_BALANCED_COLOR : DEPOSIT_UNBALANCED_COLOR
  const balanceStatusText = getBalanceStatusText({ balanced, totalToDeposit, totalDeposited })
  const resolvedBankOptions = React.useMemo(() => {
    const options = new Set()

    bankOptions.forEach((bank) => {
      const label = getBankOptionLabel(bank)
      if (label) options.add(label)
    })

    initialReceiptRows.forEach((row) => {
      if (row.bank && row.bank !== '-') options.add(row.bank)
    })

    if (draftRow?.bank && draftRow.bank !== '-') {
      options.add(draftRow.bank)
    }

    return Array.from(options)
  }, [bankOptions, draftRow?.bank, initialReceiptRows])
  const currentEditingRow = React.useMemo(
    () => receiptRows.find((row) => row.id === editingRowId) || null,
    [editingRowId, receiptRows]
  )
  const amountAvailableForDeposit = Math.max(
    0,
    totalToDeposit -
      (totalDeposited -
        (renderedDepositDialog === 'edit' ? Number(currentEditingRow?.amount || 0) : 0))
  )
  const draftDepositAmount = parseDepositAmount(draftRow?.amount)
  const amountPendingForDeposit = Math.max(
    0,
    amountAvailableForDeposit - (Number.isFinite(draftDepositAmount) ? draftDepositAmount : 0)
  )
  const hasUnsavedChanges =
    Boolean(currentEditingRow) &&
    Boolean(draftRow) &&
    !areReceiptRowsEqual(currentEditingRow, draftRow)

  React.useEffect(() => {
    setReceiptRows(initialReceiptRows)
    setEditingRowId(null)
    setDraftRow(null)
    setDraftErrors({})
    setPendingNavigationHref(null)
    setShowDiscardDialog(false)
  }, [initialReceiptRows])

  React.useEffect(() => {
    if (discardChangesToken === 0) return
    resetEditingState()
  }, [discardChangesToken])

  React.useEffect(() => {
    onDirtyChange(hasUnsavedChanges)
  }, [hasUnsavedChanges, onDirtyChange])

  React.useEffect(() => {
    onDepositsChange(serializeDepositsForApproval(receiptRows))
  }, [onDepositsChange, receiptRows])

  React.useEffect(
    () => () => {
      onDirtyChange(false)
    },
    [onDirtyChange]
  )

  React.useEffect(() => {
    if (!hasUnsavedChanges) return undefined

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }

    const handleDocumentClick = (event) => {
      const anchor = event.target.closest('a[href]')

      if (!anchor) return

      const href = anchor.getAttribute('href')

      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return

      event.preventDefault()
      event.stopPropagation()
      setPendingNavigationHref(anchor.href)
      setShowDiscardDialog(true)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('click', handleDocumentClick, true)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('click', handleDocumentClick, true)
    }
  }, [hasUnsavedChanges])

  const startEditingRow = (row) => {
    if (!canWrite || isApproved || isSavingDeposits || depositsSaving) return

    if (editingRowId && editingRowId !== row.id && hasUnsavedChanges) {
      return
    }

    setEditingRowId(row.id)
    setDraftRow(createReceiptDraft(row))
    setDraftErrors({})
    setReceiptImage('')
    setReceiptImages([])
    setDepositDialog('edit')
  }

  const startAddingDeposit = () => {
    setEditingRowId(null)
    setDraftRow({ bank: '', number: '', date: '', amount: '' })
    setDraftErrors({})
    setReceiptImage('')
    setReceiptImages([])
    setDepositDialog('create')
  }

  const closeDepositDialog = () => {
    if (isSavingDeposits) return

    setDepositDialog(null)
  }

  const handleDepositDialogExited = () => {
    setReceiptImage('')
    setReceiptImages([])
    resetEditingState()
  }

  const handleReceiptImagesChange = (imageList) => {
    setReceiptImages(imageList)
    const nextReceiptImage = imageList[0]?.data_url || ''
    setReceiptImage(nextReceiptImage)

    if (nextReceiptImage) {
      setDraftErrors((current) => {
        const { image: _image, ...rest } = current
        return rest
      })
    }
  }

  const handleSaveDepositDialog = async () => {
    const validationErrors = { ...validateDepositDraft(draftRow) }
    if (depositDialog === 'create' && !receiptImage) {
      validationErrors.image = SETTLEMENT_DETAILS_DEPOSITS_REQUIRED_FIELD_MESSAGE
    }
    if (draftDepositAmount > amountAvailableForDeposit) {
      validationErrors.amount = getDepositAmountExceededMessage(amountAvailableForDeposit)
    }
    if (Object.keys(validationErrors).length) return setDraftErrors(validationErrors)

    const selectedBank =
      bankOptions.find((bank) => getBankOptionLabel(bank) === draftRow.bank) || {}
    const payload = {
      bank_uid: selectedBank.uid || currentEditingRow?.bank_uid,
      bank_name: selectedBank.name || selectedBank.bank_name || draftRow.bank,
      short_name: selectedBank.short_name || currentEditingRow?.short_name || draftRow.bank,
      number: normalizeDepositNumber(draftRow.number),
      deposited_at: draftRow.date,
      amount: parseDepositAmount(draftRow.amount),
      ...(depositDialog === 'create' && receiptImage ? { image_data: receiptImage } : {})
    }
    setIsSavingDeposits(true)
    try {
      if (depositDialog === 'create') {
        await onCreateDeposit?.(payload)
        onNotify('El depósito se agregó correctamente.')
      } else {
        await onUpdateDeposit?.(currentEditingRow.id, payload)
        const changes = getDepositUiChanges(currentEditingRow, draftRow)
        if (Object.keys(changes).length > 0) {
          setEditedDepositChanges((current) => ({
            ...current,
            [getDepositUiChangeKey(currentEditingRow)]: changes
          }))
        }
        onNotify('El depósito se actualizó correctamente.')
      }
      setDepositDialog(null)
    } catch (_error) {
      onNotify(SETTLEMENT_DETAILS_DEPOSIT_UPDATE_ERROR, 'error')
    } finally {
      setIsSavingDeposits(false)
    }
  }

  const handleDeleteDeposit = async () => {
    if (!currentEditingRow || isSavingDeposits) return
    setIsSavingDeposits(true)
    try {
      await onDeleteDeposit?.(currentEditingRow.id)
      setEditedDepositChanges((current) => {
        const { [getDepositUiChangeKey(currentEditingRow)]: _removed, ...rest } = current
        return rest
      })
      onNotify('El depósito se eliminó correctamente.')
      setShowDeleteDialog(false)
      resetEditingState()
    } catch (_error) {
      onNotify(SETTLEMENT_DETAILS_DEPOSIT_UPDATE_ERROR, 'error')
    } finally {
      setIsSavingDeposits(false)
    }
  }

  const updateDraftField = (field, value) => {
    let nextValue = value

    if (field === 'number') {
      nextValue = normalizeDepositNumber(value)
    } else if (field === 'amount') {
      nextValue = normalizeDepositAmountInput(value)
    }
    const amountExceedsAvailable =
      field === 'amount' &&
      String(nextValue ?? '').trim() !== '' &&
      !Number.isNaN(parseDepositAmount(nextValue)) &&
      parseDepositAmount(nextValue) > amountAvailableForDeposit

    setDraftRow((current) => ({
      ...current,
      [field]: nextValue
    }))

    setDraftErrors((current) => {
      if (amountExceedsAvailable) {
        return {
          ...current,
          amount: getDepositAmountExceededMessage(amountAvailableForDeposit)
        }
      }

      if (!current[field] || isDepositDraftFieldInvalid(field, nextValue)) return current

      const { [field]: _removedError, ...nextErrors } = current
      return nextErrors
    })
  }

  const handleDiscardChanges = () => {
    resetEditingState()

    if (pendingNavigationHref) {
      const nextHref = pendingNavigationHref
      setPendingNavigationHref(null)
      window.location.assign(nextHref)
    }
  }

  const handleCloseDiscardDialog = () => {
    setPendingNavigationHref(null)
    setShowDiscardDialog(false)
  }

  function resetEditingState() {
    setEditingRowId(null)
    setDraftRow(null)
    setDraftErrors({})
    setPendingNavigationHref(null)
    setShowDiscardDialog(false)
  }

  return (
    <Box>
      <SettlementDetailContextHeader
        title={SETTLEMENT_DETAILS_TAB_TITLE}
        settlement={settlement}
        actions={actions}
        showUserLabel={false}
      />

      {canWrite &&
        !isApproved &&
        (balanced ? (
          <GuidanceNote
            icon={<InfoOutlinedIcon />}
            title={SETTLEMENT_DETAILS_NOTE_TITLE}
            message={SETTLEMENT_DETAILS_NOTE_MESSAGE}
            accentColor={UI_COLOR_PRIMARY_HOVER}
            backgroundColor={UI_COLOR_SURFACE_INFO}
            textColor={UI_COLOR_TEXT_INFO}
            singleLine
            sx={{ mb: 2 }}
          />
        ) : (
          <GuidanceNote
            icon={<InfoOutlinedIcon />}
            title={'Hay dinero cobrado sin relacionar a un depósito.'}
            message="Agrega los depósitos que falten hasta que el total depositado cuadre con el efectivo y cheques cobrados."
            accentColor={UI_COLOR_WARNING_TEXT}
            backgroundColor={UI_COLOR_WARNING_SOFT}
            textColor={UI_COLOR_WARNING_TEXT}
            singleLine
            sx={{ mb: 2 }}
          />
        ))}

      <DetailsSummarySection
        summaryFields={liquidationDetailsFields}
        summaryColumns={LIQUIDATION_DETAILS_GRID_COLUMNS}
        summaryTitle={SETTLEMENT_DETAILS_LIQUIDATION_SECTION_TITLE}
        summaryOpenLabel={SETTLEMENT_DETAILS_LIQUIDATION_OPEN_LABEL}
        summaryCloseLabel={SETTLEMENT_DETAILS_LIQUIDATION_CLOSE_LABEL}
      />

      <SectionCollapse
        title={SETTLEMENT_DETAILS_PAYMENT_METHODS_SECTION_TITLE}
        type="title"
        defaultOpen
        ariaLabelOpen={SETTLEMENT_DETAILS_PAYMENT_METHODS_OPEN_LABEL}
        ariaLabelClose={SETTLEMENT_DETAILS_PAYMENT_METHODS_CLOSE_LABEL}
        icon={<GoogleIcon name="payments" size={20} color={UI_COLOR_PRIMARY_ALT} />}
      >
        <PaymentMethodsSummary methods={methods} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
            mt: 1.5
          }}
        >
          <MetricCard
            icon="attach_money"
            title={SETTLEMENT_DETAILS_TOTAL_COLLECTED_LABEL}
            subtitle={SETTLEMENT_DETAILS_TOTAL_COLLECTED_SUBTITLE}
            amount={totalCollected}
          />
          <MetricCard
            icon="account_balance_wallet"
            title={SETTLEMENT_DETAILS_DELIVERY_CREDIT_TITLE}
            subtitle={SETTLEMENT_DETAILS_DELIVERY_CREDIT_SUBTITLE}
            amount={deliveryCreditAmount}
            accent={UI_COLOR_ACCENT_ORANGE_ALT}
          />
        </Box>

        {policeEnabled && <PolicePaymentCard amount={policePaymentAmount} />}
      </SectionCollapse>

      <CashCountSummary settlement={settlement} />

      <SectionCollapse
        title={SETTLEMENT_DETAILS_SUMMARY_SECTION_TITLE}
        type="title"
        defaultOpen
        ariaLabelOpen={SETTLEMENT_DETAILS_SUMMARY_OPEN_LABEL}
        ariaLabelClose={SETTLEMENT_DETAILS_SUMMARY_CLOSE_LABEL}
        icon={<GoogleIcon name="account_balance" size={20} color={UI_COLOR_PRIMARY_ALT} />}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3
          }}
        >
          <Box>
            <SummaryLine
              label={SETTLEMENT_DETAILS_COLLECTED_CASH_LABEL}
              amount={settlement?.cash_amount}
            />
            <SummaryLine
              label={SETTLEMENT_DETAILS_COLLECTED_CHECKS_LABEL}
              amount={settlement?.cheque_amount}
            />
            <Divider sx={{ my: 1 }} />
            <SummaryLine
              label={SETTLEMENT_DETAILS_TOTAL_COLLECTED_LABEL}
              amount={totalCollected}
              strong
            />
          </Box>

          <Box>
            <SummaryLine
              label={SETTLEMENT_DETAILS_TOTAL_TO_DEPOSIT_LABEL}
              amount={totalToDeposit}
            />
            <SummaryLine
              label={SETTLEMENT_DETAILS_TOTAL_DEPOSITED_LABEL}
              amount={totalDeposited}
              amountColor={balanceStatusColor}
            />
            <Box
              sx={{
                color: balanceStatusColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.4,
                mt: 2
              }}
            >
              <BalanceStatusIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ color: 'inherit', fontSize: 13, fontWeight: 800 }}>
                {balanceStatusText}
              </Typography>
            </Box>
          </Box>
        </Box>
      </SectionCollapse>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontSize: 18 }}>{SETTLEMENT_DETAILS_DEPOSITS_TITLE}</Typography>
        <Button
          variant="contained"
          startIcon={<GoogleIcon name="add" size={20} />}
          disabled={
            !canWrite ||
            isApproved ||
            totalToDeposit - totalDeposited <= 0 ||
            isSavingDeposits ||
            depositsSaving
          }
          onClick={startAddingDeposit}
          sx={{ backgroundColor: UI_COLOR_ACCENT_ORANGE }}
        >
          Agregar depósito
        </Button>
      </Box>

      <Table
        hover
        responsive
        style={{
          tableLayout: 'fixed',
          width: '100%'
        }}
      >
        <colgroup>
          <col style={{ width: '24%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '24%' }} />
          <col style={{ width: '6%' }} />
          <col style={{ width: '5%' }} />
        </colgroup>
        <thead className="table-header-gray-mui">
          <tr>
            <th>{SETTLEMENT_DETAILS_DEPOSITS_HEADER_UID}</th>
            <th>{SETTLEMENT_DETAILS_DEPOSITS_HEADER_BANK}</th>
            <th>{SETTLEMENT_DETAILS_DEPOSITS_HEADER_NUMBER}</th>
            <th>{SETTLEMENT_DETAILS_DEPOSITS_HEADER_DATE}</th>
            <th>{SETTLEMENT_DETAILS_DEPOSITS_HEADER_AMOUNT}</th>
            <th>{SETTLEMENT_DETAILS_DEPOSITS_HEADER_IMAGE}</th>
            <th style={{ textAlign: 'center' }}>{SETTLEMENT_DETAILS_DEPOSITS_HEADER_ACTION}</th>
          </tr>
        </thead>

        <tbody>
          {receiptRows && receiptRows.length > 0 ? (
            receiptRows.map((row, index) => (
              <tr className="cursor-pointer" key={row.id || index}>
                <td
                  className="td-text-limited"
                  style={{ paddingLeft: 8, paddingRight: 25, verticalAlign: 'middle' }}
                >
                  {row.uid}
                </td>

                <td className="td-text-limited">
                  {row.bank || SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE}
                </td>

                <td className="td-text-limited">
                  {normalizeDepositNumber(row.number) || SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE}
                </td>

                <td className="td-text-limited">
                  {row.date ? formatDepositDate(row.date) : SETTLEMENT_DETAILS_DEPOSITS_MISSING_VALUE}
                </td>

                <td className="td-text-limited">
                  <EditedDepositCell
                    change={getDepositUiChangesForDisplay(row, editedDepositChanges).amount}
                    showChip={Boolean(
                      getDepositUiChangesForDisplay(row, editedDepositChanges).amount
                    )}
                    align="right"
                  >
                    {formatMoney(row.amount)}
                  </EditedDepositCell>
                </td>

                <td className="td-text-limited text-center">
                  {row.images && row.images.length > 0 ? (
                    <IconButton
                      size="small"
                      onClick={() =>
                        setLightbox({
                          open: true,
                          images: row.images,
                          index: 0
                        })
                      }
                      aria-label={`${SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_PREFIX} ${row.images.length} ${
                        row.images.length > 1
                          ? SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_PLURAL
                          : SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_SINGULAR
                      } ${SETTLEMENT_DETAILS_DEPOSITS_IMAGE_ARIA_SUFFIX}`}
                      sx={{
                        border: `1px solid ${UI_COLOR_PRIMARY}`,
                        borderRadius: '4px',
                        color: UI_COLOR_PRIMARY_LINK,
                        backgroundColor: UI_COLOR_WHITE,
                        padding: '5px 8px',
                        '&:hover': {
                          backgroundColor: UI_COLOR_SURFACE_PRIMARY_SOFT
                        },
                        '&.Mui-disabled': {
                          borderColor: UI_COLOR_BORDER_MEDIUM,
                          color: UI_COLOR_TEXT_ICON_MUTED,
                          backgroundColor: UI_COLOR_WHITE
                        }
                      }}
                    >
                      <Badge
                        badgeContent={row.images.length}
                        max={99}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: UI_COLOR_ACCENT_ORANGE,
                            color: UI_COLOR_WHITE,
                            fontSize: 10,
                            fontWeight: 700,
                            minWidth: 18,
                            height: 18,
                            lineHeight: '18px',
                            padding: 0
                          }
                        }}
                      >
                        <GoogleIcon name="image" size={18} color={UI_COLOR_PRIMARY} />
                      </Badge>
                    </IconButton>
                  ) : (
                    '-'
                  )}
                </td>

                <td
                  align="center"
                  style={{
                    width: ACTION_COLUMN_WIDTH,
                    paddingLeft: 6,
                    paddingRight: 6
                  }}
                >
                  <DepositRowActionButton
                    row={row}
                    disabled={
                      !canWrite ||
                      isApproved ||
                      isSavingDeposits ||
                      depositsSaving ||
                      Boolean(depositDialog)
                    }
                    onEdit={() => startEditingRow(row)}
                    onDelete={() => {
                      setEditingRowId(row.id)
                      setShowDeleteDialog(true)
                    }}
                  />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7">{SETTLEMENT_DETAILS_DEPOSITS_EMPTY_STATE}</td>
            </tr>
          )}
        </tbody>
      </Table>
      <EvidenceLightbox
        open={lightbox.open}
        images={lightbox.images}
        index={lightbox.index}
        onClose={() => setLightbox({ open: false, images: [], index: 0 })}
      />
      <TaskConfirmDialog
        open={showDiscardDialog}
        title={SETTLEMENT_DETAILS_DISCARD_CHANGES_TITLE}
        message={SETTLEMENT_DETAILS_DISCARD_CHANGES_MESSAGE}
        confirmLabel={SETTLEMENT_DETAILS_DISCARD_CHANGES_CONFIRM}
        cancelLabel={SETTLEMENT_DETAILS_DISCARD_CHANGES_CANCEL}
        confirmButtonSx={DISCARD_DIALOG_CONFIRM_BUTTON_SX}
        onClose={handleCloseDiscardDialog}
        onConfirm={handleDiscardChanges}
      />
      <FormDialog
        open={Boolean(depositDialog)}
        title={renderedDepositDialog === 'create' ? 'Agregar depósito' : 'Editar depósito'}
        subtitle={
          renderedDepositDialog === 'create'
            ? 'Registra un nuevo depósito para esta liquidación.'
            : 'Actualiza los datos del depósito en esta liquidación.'
        }
        submitLabel={renderedDepositDialog === 'create' ? 'Agregar depósito' : 'Guardar cambios'}
        loading={isSavingDeposits}
        onClose={closeDepositDialog}
        onSubmit={handleSaveDepositDialog}
        onExited={handleDepositDialogExited}
        maxWidth={false}
        paperSx={{
          maxWidth: 640
        }}
        headerBoxSx={{
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2, sm: 2.5 },
          pb: 1
        }}
        titleSx={{ fontSize: { xs: 18, sm: 20 } }}
        subtitleSx={{ fontSize: 14, mt: 0.5 }}
        contentSx={{
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: 'none'
        }}
        actionsSx={{
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          gap: 1,
          borderTop: 'none'
        }}
        cancelButtonSx={{ height: 36, minHeight: 36, px: 2, fontSize: 12 }}
        submitButtonSx={{ height: 36, minHeight: 36, px: 2, fontSize: 12 }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            columnGap: { xs: 1.5, sm: 2 },
            rowGap: { xs: 1.5, sm: 2 },
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.5, sm: 2 },
            border: `1px solid ${UI_COLOR_BORDER_CARD}`,
            borderRadius: 1
          }}
        >
          <DepositDialogField label="Banco" required>
            <EditableDepositInput
              dialog
              select
              value={draftRow?.bank || ''}
              onChange={(event) => updateDraftField('bank', event.target.value)}
              error={Boolean(draftErrors.bank)}
              helperText={draftErrors.bank}
              inputProps={{ 'aria-label': 'Banco' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                )
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) =>
                  selected || (
                    <Box
                      component="span"
                      sx={{
                        color: UI_COLOR_TEXT_MUTED_LIGHT,
                        WebkitTextFillColor: UI_COLOR_TEXT_MUTED_LIGHT,
                        fontWeight: 400,
                        opacity: 1
                      }}
                    >
                      {DEPOSIT_DIALOG_BANK_PLACEHOLDER}
                    </Box>
                  )
              }}
            >
              <MenuItem value="">{DEPOSIT_DIALOG_BANK_PLACEHOLDER}</MenuItem>
              {resolvedBankOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </EditableDepositInput>
          </DepositDialogField>

          <DepositDialogField label="Número de depósito" required>
            <EditableDepositInput
              dialog
              value={draftRow?.number || ''}
              onChange={(event) => updateDraftField('number', event.target.value)}
              error={Boolean(draftErrors.number)}
              helperText={draftErrors.number}
              placeholder={DEPOSIT_DIALOG_NUMBER_PLACEHOLDER}
              inputProps={{
                'aria-label': SETTLEMENT_DETAILS_DEPOSITS_NUMBER_INPUT_LABEL,
                maxLength: DEPOSIT_NUMBER_MAX_LENGTH
              }}
            />
          </DepositDialogField>

          <DepositDialogField label="Fecha del depósito" required>
            <EditableDepositInput
              dialog
              type="date"
              value={draftRow?.date || ''}
              onChange={(value) => updateDraftField('date', value)}
              error={Boolean(draftErrors.date)}
              helperText={draftErrors.date}
              inputProps={{ 'aria-label': 'Fecha del depósito' }}
            />
          </DepositDialogField>

          <DepositDialogField label="Monto" required>
            <EditableDepositInput
              dialog
              value={draftRow?.amount ?? ''}
              onChange={(event) => updateDraftField('amount', event.target.value)}
              onBlur={() =>
                updateDraftField('amount', formatDepositAmountForDisplay(draftRow?.amount))
              }
              error={Boolean(draftErrors.amount)}
              helperText={renderedDepositDialog === 'create' ? '' : draftErrors.amount}
              placeholder={DEPOSIT_DIALOG_AMOUNT_PLACEHOLDER}
              inputProps={{
                'aria-label': SETTLEMENT_DETAILS_DEPOSITS_AMOUNT_INPUT_LABEL,
                inputMode: 'decimal'
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">Q</InputAdornment>
              }}
            />
            {renderedDepositDialog === 'create' ? (
              <>
                <Typography sx={{ mt: 0.5, fontSize: 12, color: UI_COLOR_TEXT_SUBTITLE }}>
                  Monto pendiente por depositar: {formatMoney(amountPendingForDeposit)}
                </Typography>
                {draftErrors.amount ? (
                  <Typography sx={{ ...DEPOSIT_DIALOG_ERROR_TEXT_SX, mt: 0.5 }}>
                    {draftErrors.amount}
                  </Typography>
                ) : null}
              </>
            ) : null}
          </DepositDialogField>
        </Box>

        {renderedDepositDialog === 'create' ? (
          <DepositReceiptUploadField
            value={receiptImages}
            onChange={handleReceiptImagesChange}
            error={Boolean(draftErrors.image)}
            errorMessage={draftErrors.image}
            onViewImage={(imageSrc) =>
              setLightbox({
                open: true,
                images: [{ src: imageSrc, title: 'Comprobante' }],
                index: 0
              })
            }
          />
        ) : null}
      </FormDialog>
      <TaskConfirmDialog
        open={showDeleteDialog}
        loading={isSavingDeposits}
        title="¿Eliminar este depósito?"
        message="Vas a eliminar el depósito. Esta acción no se puede deshacer, y el monto volverá a aparecer como pendiente por depositar en la liquidación."
        confirmLabel="Eliminar depósito"
        cancelLabel="Cancelar"
        maxWidth={false}
        titleDivider
        paperSx={DELETE_DIALOG_PAPER_SX}
        titleSx={DELETE_DIALOG_TITLE_SX}
        contentSx={DELETE_DIALOG_CONTENT_SX}
        messageSx={DELETE_DIALOG_MESSAGE_SX}
        actionsSx={DELETE_DIALOG_ACTIONS_SX}
        cancelButtonSx={DELETE_DIALOG_CANCEL_BUTTON_SX}
        confirmButtonSx={DELETE_DIALOG_CONFIRM_BUTTON_SX}
        onClose={() => {
          setShowDeleteDialog(false)
          resetEditingState()
        }}
        onConfirm={handleDeleteDeposit}
      />
    </Box>
  )
}

function DepositRowActionButton({ row, onEdit, onDelete, disabled = false }) {
  const actions = [
    {
      text: SETTLEMENT_DETAILS_EDIT_ACTION_LABEL,
      handler: onEdit
    },
    {
      text: 'Eliminar',
      handler: onDelete
    }
  ]

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      style={{
        width: ACTION_COLUMN_WIDTH,
        display: 'flex',
        justifyContent: 'center',
        margin: '0 auto'
      }}
    >
      <DropdownActionButtonMui
        id={`btn-dropdown-deposit-${row.id}`}
        list={actions}
        disabled={disabled}
      />
    </div>
  )
}

function EditedDepositCell({ change, showChip = false, align = 'left', children }) {
  if (!change) return <Box sx={{ textAlign: align }}>{children}</Box>

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        gap: 1,
        whiteSpace: 'nowrap'
      }}
    >
      {showChip ? (
        <Chip
          icon={
            <GoogleIcon
              name="edit"
              size={16}
              color={UI_COLOR_DANGER}
              weight={500}
              opticalSize={20}
            />
          }
          label="Editado"
          size="small"
          sx={{
            height: 24,
            borderRadius: '12px',
            color: UI_COLOR_DANGER,
            backgroundColor: '#fff0f1',
            fontSize: 12,
            fontWeight: 700,
            '& .MuiChip-icon': {
              ml: 1,
              mr: -0.5
            },
            '& .MuiChip-label': {
              pl: 0.75,
              pr: 1.25
            }
          }}
        />
      ) : null}
      <Typography
        component="span"
        sx={{
          color: UI_COLOR_TEXT_MUTED_LIGHT,
          fontSize: 'inherit',
          textDecoration: 'line-through'
        }}
      >
        {change.previous}
      </Typography>
      <Typography component="span" sx={{ color: UI_COLOR_TEXT_HEADING, fontSize: 'inherit' }}>
        {change.current}
      </Typography>
    </Box>
  )
}

function DepositReceiptUploadField({
  value,
  onChange,
  error = false,
  errorMessage = '',
  onViewImage
}) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <DepositDialogField label="Comprobante" required>
        <ImageUploading
          value={value}
          onChange={onChange}
          maxNumber={1}
          maxFileSize={5 * 1024 * 1024}
          acceptType={['jpg', 'png']}
          dataURLKey="data_url"
        >
          {({
            imageList,
            onImageUpload,
            onImageRemove,
            isDragging,
            dragProps,
            errors: uploadErrors
          }) =>
            imageList.length > 0 ? (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 48,
                  border: `1px solid ${error ? UI_COLOR_DANGER : UI_COLOR_BORDER_CARD}`,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  px: 1.5,
                  gap: 1,
                  boxSizing: 'border-box'
                }}
              >
                <GoogleIcon name="cloud_upload" size={22} color={UI_COLOR_PRIMARY_ALT} />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 12,
                    color: UI_COLOR_TEXT_HEADING,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {imageList[0].file?.name}
                  {imageList[0].file?.size ? ` ${convertByteToMb(imageList[0].file.size)}` : ''}
                </Typography>
                <Button
                  type="button"
                  variant="text"
                  onClick={() => onViewImage?.(imageList[0].data_url)}
                  sx={{
                    minWidth: 0,
                    p: 0,
                    fontSize: 12,
                    fontWeight: 400,
                    textTransform: 'none',
                    color: UI_COLOR_PRIMARY_LINK,
                    textDecoration: 'underline',
                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                  }}
                >
                  Ver imagen
                </Button>
                <IconButton
                  type="button"
                  aria-label="Eliminar comprobante"
                  onClick={() => onImageRemove(0)}
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    width: 24,
                    height: 24,
                    p: 0,
                    backgroundColor: '#202020',
                    color: UI_COLOR_WHITE,
                    '&:hover': { backgroundColor: '#202020' }
                  }}
                >
                  <GoogleIcon name="close" size={16} color={UI_COLOR_WHITE} />
                </IconButton>
              </Box>
            ) : (
              <Box>
                <Box
                  aria-label="Comprobante"
                  sx={{
                    width: '100%',
                    p: '22px 20px',
                    borderWidth: 2,
                    borderStyle: 'dashed',
                    borderRadius: '8px',
                    borderColor: error ? UI_COLOR_DANGER : '#E0E0E0',
                    backgroundColor: isDragging ? UI_COLOR_SURFACE_PRIMARY_SOFT : '#FAFAFB',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    alignSelf: 'stretch',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    cursor: 'pointer'
                  }}
                  {...dragProps}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: UI_COLOR_SURFACE_PRIMARY_SOFT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none'
                    }}
                  >
                    <GoogleIcon name="cloud_upload" size={28} color={UI_COLOR_PRIMARY_ALT} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: UI_COLOR_TEXT_HEADING,
                      pointerEvents: 'none'
                    }}
                  >
                    Seleccione una sola imagen o arrástrela y suéltela aquí
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: UI_COLOR_TEXT_SUBTITLE,
                      pointerEvents: 'none'
                    }}
                  >
                    JPG o PNG, tamaño del archivo no mayor a 5MB
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: UI_COLOR_TEXT_SUBTITLE,
                      pointerEvents: 'none'
                    }}
                  >
                    ó
                  </Typography>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={onImageUpload}
                    sx={{
                      height: 36,
                      px: 2,
                      fontSize: 13,
                      fontWeight: 400,
                      textTransform: 'none',
                      color: UI_COLOR_PRIMARY_ALT,
                      borderColor: UI_COLOR_PRIMARY_ALT,
                      '&:hover': {
                        borderColor: UI_COLOR_PRIMARY_ALT,
                        backgroundColor: UI_COLOR_SURFACE_PRIMARY_SOFT
                      }
                    }}
                  >
                    Seleccionar imagen
                  </Button>
                </Box>
                {uploadErrors?.maxFileSize || uploadErrors?.acceptType ? (
                  <Typography sx={{ ...DEPOSIT_DIALOG_ERROR_TEXT_SX, mt: 0.5 }}>
                    Seleccione una imagen JPG o PNG de máximo 5MB
                  </Typography>
                ) : null}
              </Box>
            )
          }
        </ImageUploading>
        {errorMessage ? (
          <Typography sx={{ ...DEPOSIT_DIALOG_ERROR_TEXT_SX, mt: 0.25 }}>{errorMessage}</Typography>
        ) : null}
      </DepositDialogField>
    </Box>
  )
}

function DepositDialogField({ label, required = false, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="label"
        sx={{
          display: 'block',
          mb: 0.5,
          color: UI_COLOR_TEXT_HEADING,
          fontSize: 13,
          lineHeight: '18px',
          fontWeight: 700
        }}
      >
        {label}
        {required ? (
          <Box component="span" sx={{ ml: 0.5, color: UI_COLOR_DANGER }}>
            *
          </Box>
        ) : null}
      </Typography>
      {children}
    </Box>
  )
}

function EditableDepositInput({
  children,
  select = false,
  type = 'text',
  value,
  onChange,
  disabled = false,
  dialog = false,
  ...props
}) {
  if (type === 'date') {
    return (
      <SingleDateDepositPicker
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={Boolean(props.error)}
        helperText={props.helperText || ''}
        dialog={dialog}
        ariaLabel={props.inputProps?.['aria-label']}
      />
    )
  }

  return (
    <TextField
      select={select}
      type={type}
      value={value}
      onChange={onChange}
      disabled={disabled}
      size="small"
      fullWidth
      variant="outlined"
      {...props}
      sx={{
        display: 'block',
        width: '100%',
        minWidth: 100,
        '& .MuiOutlinedInput-root': {
          height: dialog ? DEPOSIT_DIALOG_INPUT_HEIGHT : EDITABLE_INPUT_HEIGHT,
          minHeight: dialog ? DEPOSIT_DIALOG_INPUT_HEIGHT : EDITABLE_INPUT_HEIGHT,
          maxHeight: dialog ? DEPOSIT_DIALOG_INPUT_HEIGHT : EDITABLE_INPUT_HEIGHT,
          boxSizing: 'border-box',
          borderRadius: '4px',
          color: dialog ? UI_COLOR_TEXT_HEADING : UI_COLOR_PRIMARY,
          backgroundColor: UI_COLOR_WHITE,
          '& fieldset': {
            borderColor: dialog ? UI_COLOR_BORDER_CARD : UI_COLOR_PRIMARY
          },
          '&:hover fieldset': {
            borderColor: dialog ? UI_COLOR_TEXT_SUBTITLE : UI_COLOR_PRIMARY
          },
          '&.Mui-focused fieldset': {
            borderColor: UI_COLOR_PRIMARY
          },
          '&.Mui-error fieldset, &.Mui-error:hover fieldset, &.Mui-error.Mui-focused fieldset': {
            borderColor: UI_COLOR_DANGER
          }
        },
        '& .MuiInputBase-input': {
          fontSize: dialog ? 14 : 12,
          py: 0,
          color: dialog ? UI_COLOR_TEXT_HEADING : UI_COLOR_PRIMARY,
          WebkitTextFillColor: dialog ? UI_COLOR_TEXT_HEADING : UI_COLOR_PRIMARY
        },
        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
          fontSize: dialog ? 20 : 24
        },
        '& .MuiSelect-icon': {
          color: dialog ? UI_COLOR_TEXT_SUBTITLE : UI_COLOR_PRIMARY
        },
        '& .MuiInputAdornment-root': {
          color: UI_COLOR_TEXT_SUBTITLE
        },
        '& .MuiFormHelperText-root, & .MuiFormHelperText-root.Mui-error': {
          ...DEPOSIT_DIALOG_ERROR_TEXT_SX,
          marginLeft: 0,
          marginRight: 0,
          marginTop: 0.25
        }
      }}
    >
      {children}
    </TextField>
  )
}
